import { useDispatch, useSelector } from 'react-redux';
import { GVExpansionPanel } from 'components/common';
import { GVTypography } from 'components/lib';
import {
  AccordionSummary,
  AccordionDetails,
  Grid,
  Theme,
  Box,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import { menuLightGrey, darkGreyBackground, getStyleVariables, tokenAlpha } from 'styles/vars';
import {
  getChangeGridDirection,
  getChangeGridWidth,
  getDifferencePanelOpen,
  inspection,
  getFocusedDifferenceGrids,
  getFocusedDifference,
} from 'store';
import { Rnd, DraggableData } from 'react-rnd';
import { useEffect, useState, useRef, useCallback } from 'react';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { GridCharacter } from 'types';
import DifferenceGridElement from './DifferenceGridElement';
import { makeStyles } from 'tss-react/mui';

export interface DifferenceGridLayoutProps {
  minWidth?: number;
  id?: string;
}

const useStyles = makeStyles<DifferenceGridLayoutProps & { changeGridDirection: string }>()((theme: Theme, props) => {
  const styleVariables = getStyleVariables(theme);
  return {
    root: {
      backgroundColor: menuLightGrey,
      width: '100%',
      height: 'auto',
      borderRadius: '4px',
    },
    iconGrid: {
      maxWidth: '16px',
      maxHeight: '16px',
      marginLeft: theme.spacing(1),
    },
    expansionSummaryRoot: {
      padding: theme.spacing(0, 1),
      height: '40px',
      minHeight: '40px !important',
      maxHeight: '40px',
      '&.Mui-expanded': {
        height: '40px',
        minHeight: '40px !important',
        maxHeight: '40px',
      },
      '& .MuiAccordionSummary-content': {
        margin: '0 !important',
      },
      '& .no-drag': {
        cursor: 'pointer',
        pointerEvents: 'auto',
      },
      '& .MuiAccordionSummary-expandIconWrapper': {
        cursor: 'pointer',
        pointerEvents: 'auto',
      },
    },
    contentContainer: {
      backgroundColor: darkGreyBackground,
      borderRadius: '4px',
      width: '100%',
      overflowY: 'hidden',
      padding: theme.spacing(0.5),
      ...styleVariables.scrollBar,
    },
    rndContainer: {
      '& .resize-handle': {
        cursor: 'col-resize !important',
      },
      '& .drag-handle': {
        cursor: 'grab !important',
        '&:active': {
          cursor: 'grabbing !important',
        },
      },
      '&.dragging': {
        cursor: 'grabbing !important',
        '& .drag-handle': {
          cursor: 'grabbing !important',
        },
        '& *': {
          cursor: 'grabbing !important',
        },
      },
    },
    expansionDetailsRoot: {
      padding: theme.spacing(0, 1, 1, 1),
      width: '100%',
      overflow: 'visible',
    },
    divider: {
      backgroundColor: tokenAlpha('var(--verify-color-highlight)', 0.5),
    },
    buttonGroup: {
      backgroundColor: 'transparent',
      '& .MuiToggleButton-root': {
        border: 0,
        backgroundColor: 'transparent',
        padding: theme.spacing(0.3),
        height: 'auto',
        color: theme.palette.text.primary,
        pointerEvents: 'auto',
        '&.Mui-selected': {
          backgroundColor: tokenAlpha('var(--verify-color-highlight)', 0.5),
          color: theme.palette.text.primary,
        },
        '&.Mui-disabled': {
          color: tokenAlpha(theme.palette.action.disabled, 0.5),
        },
        '&:hover': {
          textDecoration: 'none',
          backgroundColor: tokenAlpha('var(--verify-color-highlight)', 0.5),
          '@media (hover: none)': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    diffElementContainer: {
      display: 'flex',
      flexDirection: 'column',
      whiteSpace: 'nowrap',
      width: 'fit-content',
      marginTop: 0,
      alignItems: 'flex-start',
    },
    sectionContainer: {
      width: '100%',
      marginBottom: theme.spacing(0.5),
    },
    dragArea: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginRight: theme.spacing(1),
    },
  };
});

const DifferenceGridLayout = ({ minWidth = 400, id = '' }: DifferenceGridLayoutProps) => {
  const changeGridDirection = useSelector(getChangeGridDirection);
  const { classes } = useStyles({ minWidth, id, changeGridDirection });
  const openChangeGrid = useSelector(getDifferencePanelOpen);
  const currentDifference = useSelector(getFocusedDifference);
  const persistedWidth = useSelector(getChangeGridWidth);
  const dispatch = useDispatch();
  const { masterGrid, sampleGrid } = useSelector(getFocusedDifferenceGrids);

  const previousDifferenceId = useRef<string | null>(null);
  const [position, setPosition] = useState({ x: 340, y: -105 });
  const [size, setSize] = useState({ width: persistedWidth || 400, height: 140 });
  const [needsScrollbar, setNeedsScrollbar] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const previousOpenState = useRef<boolean | null>(null);
  const rndRef = useRef<Rnd>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [tooltipContainer, setTooltipContainer] = useState<HTMLDivElement | null>(null);

  // The width of a single character cell in the grid (in pixels)
  const GRID_CHARACTER_WIDTH = 20;
  // The additional padding added to the grid container (in pixels)
  const GRID_PADDING = 25;

  const calculateDynamicWidth = useCallback(
    (currentResizeWidth?: number) => {
      const maxContentLength = Math.max(masterGrid?.length || 0, sampleGrid?.length || 0);
      const contentWidth = maxContentLength * GRID_CHARACTER_WIDTH + GRID_PADDING;
      const maxContainerWidth = window.innerWidth - 400;
      // Calculate the ideal width based on content
      const calculatedWidth = Math.max(minWidth, Math.min(contentWidth, maxContainerWidth));
      // If user has set a width preference, use it; otherwise calculate based on content
      // Scrollbar will handle overflow when content is wider than user's preference
      const finalContainerWidth = persistedWidth || calculatedWidth;
      // When actively resizing, use the current resize width for scrollbar calculation
      const widthForScrollbar = currentResizeWidth ?? finalContainerWidth;
      const needsScrollbar = contentWidth > widthForScrollbar;
      return { containerWidth: finalContainerWidth, needsScrollbar };
    },
    [masterGrid.length, sampleGrid.length, minWidth, persistedWidth],
  );

  const handleResize = useCallback(() => {
    if (openChangeGrid) {
      const { containerWidth, needsScrollbar } = calculateDynamicWidth();
      setSize((prevSize) => ({ ...prevSize, width: containerWidth }));
      setNeedsScrollbar(needsScrollbar);
    }
  }, [openChangeGrid, calculateDynamicWidth]);

  // Helper to handle difference changes and initial positioning
  const handleDifferenceChangePositioning = useCallback(
    (options?: { immediate?: boolean; x?: number; yScrollbar?: number; yNoScrollbar?: number }) => {
      const { immediate = false, x = 360, yScrollbar = -110, yNoScrollbar = -105 } = options || {};

      const { containerWidth, needsScrollbar } = calculateDynamicWidth();
      const baseY = needsScrollbar ? yScrollbar : yNoScrollbar;

      if (immediate) {
        // Immediate positioning without render state management
        setPosition({ x, y: baseY });
        setSize({ width: containerWidth, height: 140 });
        previousDifferenceId.current = currentDifference?.id || null;
        dispatch(inspection.actions.setDifferencePanelOpen(true));
        previousOpenState.current = true;
      } else {
        // Unmount first to prevent position shifting during state updates
        setShouldRender(false);

        // Update state without rendering to ensure correct initial position
        setPosition({ x, y: baseY });
        setSize({ width: containerWidth, height: 140 });

        // Wait for state updates to complete before mounting
        Promise.resolve().then(() => {
          previousDifferenceId.current = currentDifference?.id || null;
          dispatch(inspection.actions.setDifferencePanelOpen(true));
          previousOpenState.current = true;
          setShouldRender(true);
        });
      }
    },
    [calculateDynamicWidth, dispatch, currentDifference],
  );

  // Effect to handle difference changes and initial positioning
  useEffect(() => {
    if (currentDifference?.id !== previousDifferenceId.current) {
      handleDifferenceChangePositioning();
    }
  }, [currentDifference?.id, handleDifferenceChangePositioning]);

  // Effect to handle window resize events
  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleInteractionStyles(false);
      document.body.style.cursor = '';
      const style = document.getElementById('drag-style');
      if (style) {
        style.remove();
      }
    };
  }, [handleResize]);

  // Handle immediate initial positioning for new differences
  useEffect(() => {
    if (currentDifference?.id !== previousDifferenceId.current) {
      handleDifferenceChangePositioning({
        immediate: true,
        x: 340,
        yScrollbar: -115,
        yNoScrollbar: -105,
      });
    }
  }, [currentDifference?.id, handleDifferenceChangePositioning]);

  // Update width for expanded differences
  useEffect(() => {
    if (currentDifference?.id && openChangeGrid) {
      if (size.width === 400) {
        const { containerWidth } = calculateDynamicWidth();
        setSize({ width: containerWidth, height: 140 });
      }
    }
  }, [currentDifference?.id, openChangeGrid]);

  // Handle expand/collapse transitions
  useEffect(() => {
    if (previousOpenState.current !== null && previousOpenState.current !== openChangeGrid) {
      const { needsScrollbar, containerWidth } = calculateDynamicWidth();
      const baseY = needsScrollbar ? -110 : -105;

      if (openChangeGrid) {
        setPosition((prev) => ({
          ...prev,
          y: prev.y - (needsScrollbar ? 130 : 125),
        }));
      } else {
        // When collapsing, snap back to default position
        setPosition({
          x: 340,
          y: baseY + (needsScrollbar ? 130 : 125),
        });
      }

      setSize((prev) => ({
        ...prev,
        height: openChangeGrid ? 140 : 40,
        width: containerWidth,
      }));

      setTimeout(() => {
        if (rndRef.current && rndRef.current.updateSize) {
          rndRef.current.updateSize(size);
        }
      }, 100);
    }
    previousOpenState.current = openChangeGrid;
  }, [openChangeGrid]);

  // Effect to update width when switching to a different difference
  // Only runs when difference ID changes, not when user resizes
  useEffect(() => {
    if (openChangeGrid && currentDifference?.id) {
      const { containerWidth, needsScrollbar } = calculateDynamicWidth();
      setSize((prevSize) => ({ ...prevSize, width: containerWidth }));
      setNeedsScrollbar(needsScrollbar);
    }
  }, [currentDifference?.id, openChangeGrid]);

  // Effect to handle scrollbar position and content alignment based on reading direction
  useEffect(() => {
    if (contentRef.current && needsScrollbar) {
      const container = contentRef.current;
      if (changeGridDirection === 'rtl') {
        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollLeft = maxScroll;
      } else {
        container.scrollLeft = 0;
      }
    }
  }, [needsScrollbar, changeGridDirection, currentDifference?.id, openChangeGrid, size.width]);

  const handleInteractionStyles = (isStart: boolean) => {
    document.body.style.userSelect = isStart ? 'none' : '';
    document.body.style.pointerEvents = isStart ? 'none' : '';
  };

  const rndHandlers = {
    onDragStart: () => {
      setIsDragging(true);
      handleInteractionStyles(true);
      document.body.style.cursor = 'grabbing !important';
      const style = document.createElement('style');
      style.id = 'drag-style';
      style.innerHTML = '* { cursor: grabbing !important; }';
      document.head.appendChild(style);
    },

    onDragStop: (_e: any, d: DraggableData) => {
      setPosition({ x: d.x, y: d.y });

      if (openChangeGrid && rndRef.current) {
        const element = rndRef.current.resizableElement.current;
        if (element) {
          const rect = element.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const elementBottom = rect.bottom;
          const safeBottom = windowHeight - 25;

          if (elementBottom > safeBottom) {
            const correction = elementBottom - safeBottom;
            const newY = d.y - correction;
            setPosition({ x: d.x, y: newY });
          }
        }
      }

      setIsDragging(false);
      handleInteractionStyles(false);
      document.body.style.cursor = '';
      const style = document.getElementById('drag-style');
      if (style) {
        style.remove();
      }
    },
    onResizeStart: () => {
      setIsResizing(true);
      handleInteractionStyles(true);
    },
    onResize: (_e: MouseEvent | TouchEvent, _direction: string, ref: HTMLElement) => {
      const width = ref.offsetWidth;
      setSize((prevSize) => ({ ...prevSize, width }));

      const { needsScrollbar } = calculateDynamicWidth(width);
      setNeedsScrollbar(needsScrollbar);
    },
    onResizeStop: (_e: MouseEvent | TouchEvent, _direction: string, ref: HTMLElement) => {
      const width = ref.offsetWidth;
      setSize((prevSize) => ({ ...prevSize, width }));
      // Persist width to Redux so it's remembered across difference selections
      dispatch(inspection.actions.setChangeGridWidth(width));
      setIsResizing(false);
      handleInteractionStyles(false);
    },
  };

  const getSortedGrid = (grid: GridCharacter[]) => {
    if (changeGridDirection === 'rtl') {
      return [...grid].sort((a, b) => b.index - a.index);
    }
    return grid;
  };

  const handleAlignment = (event: React.MouseEvent, newAlignment: string | null) => {
    if (newAlignment !== null) {
      dispatch(inspection.actions.setChangeGridDirection(newAlignment));
    }
    event.stopPropagation();
  };

  const renderGridSection = (grid: GridCharacter[], sectionType: 'source' | 'target') => {
    const pageNumber = currentDifference?.[sectionType]?.location.pageNumber;
    const text = currentDifference?.[sectionType]?.text;
    const label = sectionType === 'source' ? 'Source' : 'New';

    return (
      <div className={classes.sectionContainer} key={`${currentDifference?.id}-${sectionType}`}>
        <div style={{ position: 'relative', width: '100%' }}>
          {/* Scrollable Content Container */}
          <div className={classes.diffElementContainer} id={`${sectionType}-grid-differences-container`}>
            {/* Sticky Header */}
            <div
              style={{
                position: 'sticky',
                [changeGridDirection === 'rtl' ? 'right' : 'left']: 0,
                top: 0,
                zIndex: 10,
                backgroundColor: darkGreyBackground,
                padding: '2px 8px',
                borderRadius: '4px',
                marginBottom: '4px',
                width: 'fit-content',
                alignSelf: changeGridDirection === 'rtl' ? 'flex-end' : 'flex-start',
              }}
            >
              <GVTypography
                emphasis="medium"
                variant="body1"
                style={{
                  textAlign: changeGridDirection === 'rtl' ? 'right' : 'left',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${label} (Page ${pageNumber})`}
              </GVTypography>
            </div>
            {/* Content */}
            <div
              style={{
                display: 'flex',
                justifyContent: changeGridDirection === 'rtl' ? 'flex-end' : 'flex-start',
                width: 'fit-content',
              }}
            >
              {getSortedGrid(grid).map((char, index) => (
                <DifferenceGridElement
                  tooltipContainer={tooltipContainer}
                  key={`${currentDifference?.id}-${char.character}-${sectionType}-${text}-${index}`}
                  difference={char.diff}
                  character={char.character}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Skip render if no difference or component not ready
  if (!currentDifference?.id || !shouldRender) {
    return null;
  }

  return (
    <Rnd
      ref={rndRef}
      size={size}
      position={position}
      {...rndHandlers}
      style={{
        position: 'absolute',
        zIndex: 1,
        transform: 'scale(1)',
        transition: !isResizing && !isDragging ? 'width 0.15s ease-out, transform 0.15s ease-out' : 'none',
        cursor: isDragging ? 'grabbing' : 'auto',
      }}
      className={`${classes.rndContainer} ${isDragging ? 'dragging' : ''}`}
      minWidth={minWidth}
      minHeight={openChangeGrid ? 140 : 40}
      dragHandleClassName="drag-handle"
      bounds="window"
      enableUserSelectHack={true}
      enableResizing={{
        top: false,
        right: true,
        bottom: false,
        left: false,
        topRight: false,
        bottomRight: false,
        bottomLeft: false,
        topLeft: false,
      }}
      cancel=".no-drag"
      resizeHandleClasses={{
        right: 'resize-handle',
      }}
    >
      <GVExpansionPanel className={classes.root} id={id} expanded={openChangeGrid} onChange={() => {}}>
        <AccordionSummary
          className={`${classes.expansionSummaryRoot} drag-handle`}
          id="DifferenceGridExpansionPanel"
          expandIcon={
            <ExpandLessIcon
              color="primary"
              id="DifferenceGridIcon"
              className="no-drag"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(inspection.actions.setDifferencePanelOpen(!openChangeGrid));
              }}
              style={{ cursor: 'pointer' }}
            />
          }
          onClick={(e) => e.preventDefault()}
        >
          <Grid container>
            <Grid item container alignItems="center" xs>
              <Grid item>
                <GVTypography emphasis="superhigh" variant="subtitle1">
                  Change grid
                </GVTypography>
              </Grid>
              <Grid item className={`${classes.iconGrid} no-drag`} data-testid="change-grid-tooltip">
                <GVTooltip
                  title='The Change Grid is applicable to the "Content, Space, Case and Hyphen" difference types.'
                  side="top"
                  align="start"
                >
                  <InfoOutlinedIcon fontSize="small" />
                </GVTooltip>
              </Grid>
            </Grid>
            <Grid item container alignItems="center" xs className="drag-handle">
              <Grid item>
                <Box width="8px" height="8px" bgcolor="error.main" sx={{ mr: 1 }} />
              </Grid>
              <Grid item>
                <GVTypography emphasis="superhigh" variant="subtitle1">
                  Changes
                </GVTypography>
              </Grid>
              <Grid item className={`${classes.dragArea} drag-handle`} />
            </Grid>
            <Grid
              item
              container
              alignItems="center"
              justifyContent="flex-end"
              sx={{ width: 'auto' }}
              className="no-drag"
            >
              <Divider flexItem orientation="vertical" className={classes.divider} />
              <Grid item>
                <ToggleButtonGroup
                  size="small"
                  value={changeGridDirection}
                  className={classes.buttonGroup}
                  exclusive
                  onChange={handleAlignment}
                >
                  <ToggleButton value="ltr">
                    <FormatAlignLeftIcon fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="rtl">
                    <FormatAlignRightIcon fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Divider flexItem orientation="vertical" className={classes.divider} />
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails className={classes.expansionDetailsRoot}>
          <div
            ref={contentRef}
            className={classes.contentContainer}
            key={currentDifference?.id}
            style={{
              overflowX: needsScrollbar ? 'auto' : 'hidden',
              textAlign: changeGridDirection === 'rtl' ? 'right' : 'left',
            }}
          >
            <div
              ref={setTooltipContainer}
              style={{
                display: 'inline-block',
                textAlign: 'left',
                width: 'fit-content',
              }}
            >
              {renderGridSection(masterGrid, 'source')}
              {renderGridSection(sampleGrid, 'target')}
            </div>
          </div>
        </AccordionDetails>
      </GVExpansionPanel>
    </Rnd>
  );
};

export default DifferenceGridLayout;
